import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import MatchPreferences from './DealWizardSteps/MatchPreferences';

// Query to fetch current user with match preferences
const GET_USER_WITH_MATCH_PREFERENCES = gql`
  query GetUserWithMatchPreferences {
    getCurrentUser {
      id
      name
      email
      locale
      motivation
      match_preference {
        id
        affiliation_weight
        product_mix_weight
        part_of_team_weight
        distance_weight
        length_of_service_weight
        length_of_service_min
        length_of_service_max
        aum_weight
        aum_min
        aum_max
        avg_age_of_client_weight
        avg_age_of_client_min
        avg_age_of_client_max
        proximity_max
        affiliate_network_weight
      }
      inventory_user {
        id
      }
    }
  }
`;

/**
 * Modal wrapper for the MatchPreferences component.
 * Used in the AI flow after teaser approval and for editing preferences from the Deal Room.
 */
const MatchPreferencesModal = ({ isOpen, onClose, onSave, currentUser: propCurrentUser, company }) => {
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch fresh user data with match preferences when modal opens
  const { data, loading, refetch } = useQuery(GET_USER_WITH_MATCH_PREFERENCES, {
    skip: !isOpen,
    fetchPolicy: 'network-only', // Always fetch fresh data
  });

  // Use fetched data if available, otherwise fall back to prop
  const currentUser = data?.getCurrentUser || propCurrentUser;

  const updateFormData = useCallback((data) => {
    setFormData(prev => ({ ...prev, ...data }));
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // The MatchPreferences component already handles saving via its internal mutation
      // We just need to wait a moment for any pending saves to complete
      await new Promise(resolve => setTimeout(resolve, 1500));

      if (onSave) {
        onSave(formData);
      }
      onClose();
    } catch (error) {
      console.error('Error saving match preferences:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  if (!isOpen) return null;

  // Show loading state while fetching data
  if (loading) {
    return (
      <div className="fixed inset-0" style={{ zIndex: 99999 }}>
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-succession-bg-primary rounded-2xl p-8 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-succession-primary mb-4"></div>
            <p className="text-succession-text-secondary">Loading preferences...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0" style={{ zIndex: 99999 }}>
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleSkip}
      />

      {/* Modal Container - centered */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="relative w-full max-w-5xl bg-succession-bg-primary rounded-2xl shadow-2xl flex flex-col pointer-events-auto"
          style={{ height: '90vh' }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-succession-primary px-8 py-6 rounded-t-2xl" style={{ flexShrink: 0 }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">
                  <FormattedMessage
                    id="succession_deals.match_preferences_modal.title"
                    defaultMessage="Set Your Match Preferences"
                  />
                </h2>
                <p className="text-white/80 text-sm mt-1">
                  <FormattedMessage
                    id="succession_deals.match_preferences_modal.description"
                    defaultMessage="Define the criteria for your ideal buyer to improve match quality."
                  />
                </p>
              </div>
              <button
                onClick={handleSkip}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="Close"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="p-8" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <MatchPreferences
              key={currentUser?.match_preference?.id || 'new'}
              formData={formData}
              updateFormData={updateFormData}
              errors={{}}
              currentUser={currentUser}
              company={company}
            />
          </div>

          {/* Footer with Actions */}
          <div className="border-t border-succession-border bg-succession-bg-secondary px-8 py-4 rounded-b-2xl" style={{ flexShrink: 0 }}>
            <div className="flex items-center justify-between">
              <button
                onClick={handleSkip}
                className="px-6 py-3 text-succession-text-secondary hover:text-succession-text-primary hover:bg-succession-hover-bg rounded-xl transition-all duration-200 font-medium"
              >
                <FormattedMessage
                  id="succession_deals.match_preferences_modal.skip_button"
                  defaultMessage="Skip for now"
                />
              </button>

              <div className="flex items-center space-x-3">
                {formData.match_preferences_complete && (
                  <span className="text-sm text-green-600 flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <FormattedMessage
                      id="succession_deals.match_preferences_modal.preferences_complete"
                      defaultMessage="Preferences saved"
                    />
                  </span>
                )}

                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-8 py-3 bg-succession-primary text-white rounded-xl font-semibold hover:bg-succession-primary-hover transition-all duration-200 shadow-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {isSaving ? (
                    <>
                      <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>
                        <FormattedMessage
                          id="succession_deals.match_preferences_modal.saving_button"
                          defaultMessage="Saving..."
                        />
                      </span>
                    </>
                  ) : (
                    <span>
                      <FormattedMessage
                        id="succession_deals.match_preferences_modal.done_button"
                        defaultMessage="Done"
                      />
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

MatchPreferencesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,
  currentUser: PropTypes.object,
  company: PropTypes.object
};

export default MatchPreferencesModal;
